import React, { useContext } from 'react';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Text,
  useMediaQuery,
  belowBreakpoint,
  Theme,
} from '@bechtel/gds';
import { SearchFieldStyled, BacklogHeaderContainerStyled, SeperatorStyled, SearchCanvasButtonStyled } from './styled';
import { usePlanningPermissions } from '~/hooks';
import { BacklogFilter, defaultValues } from '../types';
import {
  AllForemanId,
  UnassignedForemanId,
  DND_IDENTIFIER,
  usePlanning,
  TextWithEllipsis,
  EllipsisPlacement,
} from '@bechtel/client-common';
import { compact, isEqual } from 'lodash';
import { ThemeContext } from 'styled-components';
import { t, Trans } from '@lingui/macro';

interface BacklogHeaderProps {
  open: (e) => void;
  onChange: (e) => void;
  selectedMilestoneDraggableIds: string[];
  filterValues: BacklogFilter;
  unplanMilestones: () => void;
}

export const BacklogHeader: React.FC<BacklogHeaderProps> = ({
  open,
  onChange,
  selectedMilestoneDraggableIds,
  filterValues,
  unplanMilestones,
}) => {
  const { canPlanMilestones } = usePlanningPermissions();
  const { selectedForeman } = usePlanning();

  const isUnplanMilestonesDisabled =
    !selectedMilestoneDraggableIds ||
    selectedMilestoneDraggableIds.length <= 0 ||
    (selectedMilestoneDraggableIds &&
      selectedMilestoneDraggableIds.some(
        (id) => id.includes(DND_IDENTIFIER.Unplanned) && !id.includes(DND_IDENTIFIER.Expired)
      ));
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const {
    milestone,
    commodityTypes,
    materialStatus,
    commodityTypesCount,
    materialStatusCount,
    milestoneCount,
    expiredMilestoneFilter,
  } = filterValues;

  const isDefaultFilter =
    isEqual(defaultValues.commodityTypes, filterValues.commodityTypes) &&
    isEqual(defaultValues.milestone, filterValues.milestone) &&
    isEqual(defaultValues.materialStatus, filterValues.materialStatus) &&
    isEqual(defaultValues.expiredMilestoneFilter, filterValues.expiredMilestoneFilter);
  return (
    <BacklogHeaderContainerStyled
      isDisableHeader={selectedForeman?.id === AllForemanId || selectedForeman?.id === UnassignedForemanId}
      data-testid="backlog-header"
    >
      <Box mb={{ sm: '500' }}>
        <Text weight="600" size="400">
          <Trans>Backlog</Trans>
        </Text>
      </Box>
      <Box
        mb={{ sm: '400' }}
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'center' }}
        data-testid="backlog-search"
      >
        <SearchFieldStyled placeholder={t`Search tag IDs`} type="search" onChange={onChange} />
        <Box ml={{ sm: '400' }}>
          <SearchCanvasButtonStyled
            isDefaultFilterChange={!isDefaultFilter}
            appearance={ButtonAppearance.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={open}
          >
            <Icon icon="sliders-v" size={IconSize.MEDIUM} fillColor={!isDefaultFilter ? 'white' : ''} />
          </SearchCanvasButtonStyled>
        </Box>
      </Box>
      <Box mt={{ sm: '200' }}>
        <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
          <Trans>Commodity:</Trans>&nbsp;
          <Text weight="400" title={compact(commodityTypes)?.join(' , ')}>
            {commodityTypes.length == 0 || commodityTypes.length == commodityTypesCount
              ? t`All`
              : compact(commodityTypes)?.join(' , ')}
          </Text>
        </TextWithEllipsis>
      </Box>
      <Box mt={{ sm: '200' }}>
        <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
          <Trans>Milestone:</Trans>&nbsp;
          <Text weight="400" title={compact(milestone)?.join(' , ')}>
            {milestone.length == 0 || milestone.length == milestoneCount ? t`All` : compact(milestone)?.join(' , ')}
          </Text>
        </TextWithEllipsis>
      </Box>
      <Box mt={{ sm: '200' }}>
        <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
          <Trans>Material Status:</Trans>&nbsp;
          <Text weight="400" title={compact(materialStatus)?.join(' , ')}>
            {materialStatus.length == 0 || materialStatus.length == materialStatusCount
              ? t`All`
              : compact(materialStatus)?.join(' , ')}
          </Text>
        </TextWithEllipsis>
      </Box>
      <Box mt={{ sm: '200' }}>
        <Text size="250" weight="600">
          <Trans>Expired Milestone:</Trans>&nbsp;
        </Text>
        <Text size="250" weight="400">
          {expiredMilestoneFilter ? t`Yes` : t`No`}
        </Text>
      </Box>
      <SeperatorStyled></SeperatorStyled>
      {canPlanMilestones ? (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} data-testid="backlog-unplan">
          <Button
            size={ButtonSize.SMALL}
            appearance={ButtonAppearance.SECONDARY}
            disabled={isUnplanMilestonesDisabled}
            onClick={unplanMilestones}
          >
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" fillColor="blue.800" />
            </Box>
            <Text size={isWideScreen ? '200' : '300'} color="grey.1000">
              <Trans>Unplan Selected Milestones</Trans>
            </Text>
          </Button>
        </Box>
      ) : (
        <Text color="grey.600" weight="600" family="graphik" size="300">
          <Trans>You have read-only access. You can view plans, but can’t make any changes.</Trans>
        </Text>
      )}
    </BacklogHeaderContainerStyled>
  );
};
